import { useState, useEffect } from "react";
import { Wrench, Save } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const AdminMaintenanceTab = ({ authToken, onMessage }) => {
  const [settings, setSettings] = useState({ enabled: false, message: "We're upgrading the platform. We'll be back shortly.", estimated_return: "", contact_email: "gary@djsandmcs.com" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const headers = { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/maintenance/status`)
      .then(r => r.json()).then(setSettings)
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/maintenance`, { method: 'PUT', headers, body: JSON.stringify(settings) });
      const data = await res.json();
      if (data.status === 'success') onMessage(data.message);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="admin-maintenance-panel">
      <div className="p-6 border-b border-white/10">
        <h2 className="text-lg font-bold text-white" data-testid="admin-maintenance-heading">Maintenance Mode</h2>
        <p className="text-white/40 text-sm mt-1">When enabled, all visitors see a maintenance page. Admins can still access the full site.</p>
      </div>
      {loading ? <div className="p-12 text-center text-white/40">Loading settings...</div> : (
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between" data-testid="maintenance-toggle-row">
            <div>
              <p className="text-white font-medium">Maintenance Mode</p>
              <p className="text-white/40 text-sm">{settings.enabled ? 'Site is currently in maintenance mode' : 'Site is live and accessible'}</p>
            </div>
            <button onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${settings.enabled ? 'bg-red-500' : 'bg-white/20'}`} data-testid="maintenance-toggle">
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${settings.enabled ? 'left-[26px]' : 'left-0.5'}`} />
            </button>
          </div>
          {settings.enabled && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
              <Wrench className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm font-medium">Maintenance mode is ON. Visitors will see the maintenance page.</p>
            </div>
          )}
          <div>
            <label className="text-white/60 text-sm mb-1.5 block">Message to Visitors</label>
            <textarea value={settings.message} onChange={(e) => setSettings({ ...settings, message: e.target.value })} rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm resize-none" data-testid="maintenance-message-input" />
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1.5 block">Estimated Return Time</label>
            <input type="datetime-local" value={settings.estimated_return} onChange={(e) => setSettings({ ...settings, estimated_return: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm" data-testid="maintenance-return-input" />
            <p className="text-white/30 text-xs mt-1">Visitors see a live countdown. Leave blank to hide.</p>
          </div>
          <div>
            <label className="text-white/60 text-sm mb-1.5 block">Contact Email</label>
            <input type="email" value={settings.contact_email} onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition text-sm" data-testid="maintenance-email-input" />
          </div>
          <button onClick={save} disabled={saving}
            className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold text-sm transition flex items-center gap-2" data-testid="maintenance-save-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminMaintenanceTab;
